import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Lock, Key, Server, Eye, AlertTriangle, CheckCircle } from 'lucide-react';

export const Security = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <img 
              src="/assets/security.png"
              alt="Data security and protection"
              className="w-full h-64 object-cover rounded-2xl mb-8"
            />
            <div className="text-center">
              <h1 className="text-4xl font-bold text-slate-900 mb-4 font-heading">Security at Ariskool</h1>
              <p className="text-lg text-slate-600">Enterprise-grade security for Indian schools. Your data is safe with us.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Card>
              <CardContent className="p-6">
                <Lock className="h-8 w-8 text-teal-600 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Encryption</h3>
                <p className="text-slate-600">All data encrypted at rest (AES-256) and in transit (TLS 1.3)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Key className="h-8 w-8 text-emerald-600 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Authentication</h3>
                <p className="text-slate-600">JWT tokens, bcrypt password hashing, optional 2FA</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Server className="h-8 w-8 text-amber-600 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Infrastructure</h3>
                <p className="text-slate-600">AWS cloud hosting with automated backups and disaster recovery</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <Eye className="h-8 w-8 text-teal-600 mb-3" />
                <h3 className="text-xl font-semibold mb-2">Monitoring</h3>
                <p className="text-slate-600">24/7 security monitoring and incident response team</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">Security Measures</h2>
              
              <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">Data Protection</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Encryption at Rest:</strong> All data stored encrypted using AES-256</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Encryption in Transit:</strong> TLS 1.3 for all connections</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Database Isolation:</strong> Complete tenant separation with row-level security</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Backups:</strong> Automated daily backups with 30-day retention</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">Access Control</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Role-Based Access:</strong> Granular permissions for admins, HR, and teachers</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Password Policy:</strong> Minimum 8 characters with complexity requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Session Management:</strong> Short-lived tokens (15 min) with refresh rotation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Audit Logs:</strong> Immutable logs of all data access and modifications</span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 mb-3 mt-6">Infrastructure Security</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Cloud Hosting:</strong> AWS infrastructure with SOC 2 Type II certification</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>WAF:</strong> Web Application Firewall to block malicious traffic</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>DDoS Protection:</strong> Automated mitigation of denial-of-service attacks</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Network Isolation:</strong> VPC with private subnets and security groups</span>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">Compliance & Certifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">CERT-In Compliant</h4>
                  <p className="text-sm text-slate-600">Full compliance with CERT-In Cyber Security Directions (April 2022) — audit logging, 6-hour incident reporting, NTP synchronization</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">NIST Cybersecurity Framework</h4>
                  <p className="text-sm text-slate-600">Implementation across all 5 pillars: Identify, Protect, Detect, Respond, Recover</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">WCAG 2.1 AA</h4>
                  <p className="text-sm text-slate-600">Web Content Accessibility Guidelines compliance for inclusive access across all user interfaces</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">DPDP Act, 2023</h4>
                  <p className="text-sm text-slate-600">Digital Personal Data Protection Act compliance with consent management and data residency</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">RTI Act, 2005</h4>
                  <p className="text-sm text-slate-600">Proactive disclosures under Section 4(1)(b) with designated PIO and Appellate Authority. <a href="/rti" className="text-teal-600 hover:underline">View RTI Page</a></p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2">IT Act, 2000</h4>
                  <p className="text-sm text-slate-600">Full compliance with the Information Technology Act for electronic records and digital signatures</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">Security Practices</h2>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Regular Audits:</strong> Quarterly security assessments and penetration testing</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Vulnerability Scanning:</strong> Continuous automated scanning for threats</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Security Training:</strong> All employees complete annual security training</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Incident Response:</strong> 24/7 security team with defined response procedures</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <span><strong>Bug Bounty:</strong> Responsible disclosure program with security researchers</span>
                </li>
              </ul>
            </section>

            <section className="mb-8 p-6 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-amber-900 mb-2">Report a Security Issue</h3>
                  <p className="text-amber-800 mb-3">
                    If you discover a security vulnerability, please report it responsibly:
                  </p>
                  <ul className="space-y-1 text-amber-800">
                    <li><strong>Email:</strong> <a href="mailto:security@ariskool.com" className="underline">security@ariskool.com</a></li>
                    <li><strong>Response Time:</strong> Within 24 hours</li>
                    <li><strong>Recognition:</strong> Hall of fame for responsible disclosures</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4">Questions?</h2>
              <p className="text-slate-700 leading-relaxed">
                For security inquiries or to request our security whitepaper, contact us at <a href="mailto:security@ariskool.com" className="text-teal-600 hover:underline">security@ariskool.com</a>
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Security;
